import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService from '../services/apiService';
import { ExportConfiguration, ExportPushResult, HubSpotSearchResult, SummaryRef } from '../types';
import { logger } from '../utils/logger';

interface HubSpotExportFlowProps {
    fileId: string;
    summaryRef: SummaryRef;
    configs: ExportConfiguration[];
    summaryTitle: string;
    onBack: () => void;
    onClose: () => void;
}

type FlowStep = 'search' | 'confirm' | 'create' | 'sending' | 'success' | 'error';
type SendMode = 'link' | 'create';

const HubSpotExportFlow: React.FC<HubSpotExportFlowProps> = ({
    fileId, summaryRef, configs, summaryTitle, onBack, onClose,
}) => {
    const { t } = useTranslation();
    const apiService = useMemo(() => new ApiService(), []);
    const [step, setStep] = useState<FlowStep>('search');
    const [selectedConfigId, setSelectedConfigId] = useState(configs[0]?.id ?? '');
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    // CRM-gegevens blijven in dit deelvenster; nooit opslaan of loggen.
    const [results, setResults] = useState<HubSpotSearchResult[]>([]);
    const [selectedResult, setSelectedResult] = useState<HubSpotSearchResult | null>(null);
    const [objectName, setObjectName] = useState(summaryTitle);
    const [sendMode, setSendMode] = useState<SendMode>('link');
    const [sendResult, setSendResult] = useState<ExportPushResult | null>(null);
    const requestSeq = useRef(0);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const sending = useRef(false);

    const selectedConfig = configs.find(config => config.id === selectedConfigId);
    const canCreate = selectedConfig?.hubspot?.createIfNotFound === true;
    const targetType = selectedConfig?.hubspot?.targetObjectType;
    const objectType = t(`shareDialog.hubspot.objectTypes.${
        targetType === 'DEAL' || targetType === 'COMPANY' ? targetType : 'UNKNOWN'
    }`);
    const trimmedTerm = searchTerm.trim();

    const runSearch = useCallback(async (term: string, configId: string) => {
        const seq = ++requestSeq.current;
        setIsSearching(true);
        setSearchError(null);
        setResults([]);
        setHasSearched(false);
        try {
            const response = await apiService.searchHubSpot(fileId, summaryRef, configId, term);
            if (seq !== requestSeq.current) return;
            setResults(response);
            setHasSearched(true);
        } catch (error: any) {
            if (seq !== requestSeq.current) return;
            logger.error('HubSpot search failed with status:', error?.response?.status);
            setSearchError(error?.response?.data?.error?.toString() || t('shareDialog.hubspot.searchError'));
        } finally {
            if (seq === requestSeq.current) setIsSearching(false);
        }
    }, [apiService, fileId, summaryRef, t]);

    // Dezelfde 500 ms wachttijd als in de app. Bij andere invoer, een andere
    // configuratie of sluiten mag een oud antwoord geen resultaten terugzetten.
    useEffect(() => {
        if (step !== 'search') return;
        setResults([]);
        setHasSearched(false);
        setSearchError(null);
        setIsSearching(false);
        if (trimmedTerm && selectedConfigId) {
            debounceTimer.current = setTimeout(() => {
                debounceTimer.current = null;
                runSearch(trimmedTerm, selectedConfigId);
            }, 500);
        }
        return () => {
            if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
            debounceTimer.current = null;
            requestSeq.current += 1;
        };
    }, [trimmedTerm, selectedConfigId, step, runSearch]);

    const searchNow = () => {
        if (!trimmedTerm || !selectedConfigId || isSearching) return;
        if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
        runSearch(trimmedTerm, selectedConfigId);
    };

    const clearSearch = () => {
        requestSeq.current += 1;
        setResults([]);
        setSelectedResult(null);
        setHasSearched(false);
        setSearchError(null);
        setIsSearching(false);
    };

    const send = async (mode: SendMode) => {
        if (sending.current || !selectedConfig) return;
        if (mode === 'link' && !selectedResult) return;
        if (mode === 'create' && (!canCreate || !objectName.trim())) return;
        sending.current = true;
        setSendMode(mode);
        setSendResult(null);
        setStep('sending');
        try {
            const result = mode === 'create'
                ? await apiService.createAndLinkHubSpot(fileId, summaryRef, selectedConfigId, objectName.trim())
                : await apiService.linkHubSpot(fileId, summaryRef, selectedConfigId, selectedResult!.id);
            setSendResult(result);
            setStep(result.success ? 'success' : 'error');
        } catch (error: any) {
            logger.error('HubSpot export failed with status:', error?.response?.status);
            setSendResult({
                success: false,
                message: error?.response?.data?.error?.toString() || t('shareDialog.hubspot.error'),
            });
            setStep('error');
        } finally {
            sending.current = false;
        }
    };

    const inputClass = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500';
    const primaryButtonClass = 'px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
    const secondaryButtonClass = 'px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors';

    if (step === 'search') {
        return (
            <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">{t('shareDialog.hubspot.searchTitle')}</h3>
                {configs.length > 1 && (
                    <div>
                        <label htmlFor="hubspot-config" className="block text-sm font-medium text-gray-700 mb-2">
                            {t('shareDialog.hubspot.configLabel')}
                        </label>
                        <select id="hubspot-config" value={selectedConfigId} className={inputClass}
                            onChange={event => { clearSearch(); setSelectedConfigId(event.target.value); }}>
                            {configs.map(config => <option key={config.id} value={config.id}>{config.name}</option>)}
                        </select>
                    </div>
                )}
                <form onSubmit={event => { event.preventDefault(); searchNow(); }} className="space-y-3">
                    <div>
                        <label htmlFor="hubspot-search" className="block text-sm font-medium text-gray-700 mb-2">
                            {t('shareDialog.hubspot.searchLabel', { type: objectType })}
                        </label>
                        <input id="hubspot-search" type="text" autoFocus autoComplete="off" value={searchTerm}
                            onChange={event => {
                                if (event.target.value.trim() !== trimmedTerm) clearSearch();
                                setSearchTerm(event.target.value);
                            }} className={inputClass} />
                    </div>
                    <div className="flex justify-end space-x-2">
                        <button type="button" onClick={onBack} className={secondaryButtonClass}>{t('shareDialog.hubspot.back')}</button>
                        <button type="submit" disabled={!trimmedTerm || !selectedConfigId || isSearching} className={primaryButtonClass}>
                            {t('shareDialog.hubspot.search')}
                        </button>
                    </div>
                </form>
                {isSearching && <p role="status" className="text-sm text-gray-500">{t('shareDialog.hubspot.searching')}</p>}
                {searchError && <p role="alert" className="text-sm text-red-600">{searchError}</p>}
                {hasSearched && !isSearching && results.length === 0 && (
                    <p role="status" className="text-sm text-gray-500">{t('shareDialog.hubspot.empty')}</p>
                )}
                {results.length > 0 && (
                    <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">{t('shareDialog.hubspot.resultsLabel')}</p>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {results.map(result => (
                                <button key={result.id} type="button"
                                    onClick={() => { setSelectedResult(result); setStep('confirm'); }}
                                    className="w-full p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors text-left">
                                    <span className="block font-medium text-gray-900 text-sm break-words">
                                        {result.name || t('shareDialog.hubspot.unnamed')}
                                    </span>
                                    <span className="block text-gray-500 text-xs">{t('shareDialog.hubspot.objectId', { id: result.id })}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {canCreate && (
                    <div className="pt-4 border-t border-gray-200">
                        <button type="button" className={secondaryButtonClass} onClick={() => {
                            setSelectedResult(null);
                            setObjectName(summaryTitle);
                            setStep('create');
                        }}>{t('shareDialog.hubspot.createNew', { type: objectType })}</button>
                    </div>
                )}
            </div>
        );
    }

    if (step === 'confirm' && selectedResult) {
        return (
            <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">{t('shareDialog.hubspot.searchTitle')}</h3>
                <p className="text-sm text-gray-900">{t('shareDialog.hubspot.confirm', {
                    title: summaryTitle, name: selectedResult.name || t('shareDialog.hubspot.unnamed'),
                })}</p>
                <p className="text-xs text-gray-500">{t('shareDialog.hubspot.objectId', { id: selectedResult.id })}</p>
                <div className="flex justify-end space-x-2">
                    <button type="button" onClick={() => setStep('search')} className={secondaryButtonClass}>{t('shareDialog.hubspot.back')}</button>
                    <button type="button" onClick={() => send('link')} className={primaryButtonClass}>{t('shareDialog.hubspot.send')}</button>
                </div>
            </div>
        );
    }

    if (step === 'create') {
        return (
            <form className="space-y-4" onSubmit={event => { event.preventDefault(); send('create'); }}>
                <h3 className="text-sm font-semibold text-gray-900">{t('shareDialog.hubspot.createNew', { type: objectType })}</h3>
                <p className="text-sm text-gray-600">{t('shareDialog.hubspot.createHint', { title: summaryTitle })}</p>
                <div>
                    <label htmlFor="hubspot-name" className="block text-sm font-medium text-gray-700 mb-2">{t('shareDialog.hubspot.nameLabel')}</label>
                    <input id="hubspot-name" type="text" autoFocus required autoComplete="off" value={objectName}
                        onChange={event => setObjectName(event.target.value)} className={inputClass} />
                </div>
                <div className="flex justify-end space-x-2">
                    <button type="button" onClick={() => setStep('search')} className={secondaryButtonClass}>{t('shareDialog.hubspot.back')}</button>
                    <button type="submit" disabled={!canCreate || !objectName.trim()} className={primaryButtonClass}>{t('shareDialog.hubspot.createAndSend')}</button>
                </div>
            </form>
        );
    }

    if (step === 'sending') {
        return (
            <div role="status" className="flex flex-col items-center py-8 text-center">
                <div className="w-8 h-8 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin mb-4" />
                <p className="text-sm font-medium text-gray-900">{t('shareDialog.hubspot.busy')}</p>
            </div>
        );
    }

    if (step === 'success') {
        return (
            <div className="flex flex-col items-center py-6 text-center">
                <svg className="w-8 h-8 text-primary-600 mb-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <p role="status" className="text-sm text-gray-900">{sendResult?.message || t(`shareDialog.hubspot.${sendMode === 'create' ? 'created' : 'success'}`)}</p>
                <button type="button" onClick={onClose} className={`${secondaryButtonClass} mt-4`}>{t('shareDialog.hubspot.close')}</button>
            </div>
        );
    }

    return (
        <div className="space-y-4 py-6 text-center">
            <p role="alert" className="text-sm text-red-600">{sendResult?.message || t('shareDialog.hubspot.error')}</p>
            <button type="button" onClick={() => setStep(sendMode === 'create' ? 'create' : 'confirm')} className={secondaryButtonClass}>
                {t('shareDialog.hubspot.back')}
            </button>
        </div>
    );
};

export default HubSpotExportFlow;
